import path from 'path'

import _ from 'lodash'
import { gql } from '@apollo/client'
import { message } from 'antd'

import logger from '~/lib/logger'
import { AttachmentCreateType } from '~/__generated__/globalTypes'

export const CREATE_QUOTE_ERROR_MSG = 'Unable to create quote and upload files'
const VALID_DFM_REPORT_FILE_TYPES = ['.ppt', '.pptx']
const BYTES_IN_KILOBYTE = 1024
const MAX_KB = 20
const MAX_FILE_SIZE_BYTES = MAX_KB * Math.pow(BYTES_IN_KILOBYTE, 2)

export const finalizeUploadAndProcessPart = gql`
    mutation fileUploaderFinalizeUploadAndProcessPart($partId: Int!) {
        finalizeUploadAndProcessPart(partId: $partId) {
            partId
            status
            reason
        }
    }
`

const createQuoteMutation = gql`
    mutation createQuoteFileUploader($name: String, $description: String) {
        createQuote(input: { name: $name, description: $description }) {
            id
            name
        }
    }
`

const createQuoteModelRevisionMutation = gql`
    mutation createQmrFileUploader(
        $uploadMetadata: QuoteModelRevisionUploadInput!
        $quoteId: UUID!
        $quoteModelId: UUID
        $setAsSelectedForQuoteModel: Boolean!
    ) {
        createQuoteModelRevision(
            uploadMetadata: $uploadMetadata
            quoteId: $quoteId
            quoteModelId: $quoteModelId
            setAsSelectedForQuoteModel: $setAsSelectedForQuoteModel
        ) {
            id
            uploadUrl
            partRevision {
                id
                partId
                thumbnailUri
            }
        }
    }
`

const createQuoteAttachmentRevisionMutation = gql`
    mutation createQuoteAttachmentRevisionFileUploader(
        $uploadMetadata: AttachmentInput!
        $quoteId: UUID!
        $quoteAttachmentId: UUID
        $attachmentType: String
        $productAttachmentId: String
        $adminReplaced: Boolean
        $userId: Int
    ) {
        createQuoteAttachmentRevision(
            uploadMetadata: $uploadMetadata
            quoteId: $quoteId
            quoteAttachmentId: $quoteAttachmentId
            attachmentType: $attachmentType
            productAttachmentId: $productAttachmentId
            adminReplaced: $adminReplaced
            userId: $userId
        ) {
            id
            attachment {
                ... on Attachment {
                    id
                    fileName
                    uploadUrl
                }
            }
            asset {
                id
                fileName
                uploadUrl
            }
        }
    }
`

const markQuoteAttachmentRevisionUploadedMutation = gql`
    mutation markQuoteAttachmentRevisionUploadedFileUploader(
        $id: UUID!
        $userId: Int
    ) {
        markQuoteAttachmentRevisionUploaded(id: $id, userId: $userId) {
            id
            attachment {
                ... on Attachment {
                    id
                    fileName
                    downloadUrl
                }
            }
        }
    }
`

const createQMRAttachmentMutation = gql`
    mutation createQMRAttachmentMutation(
        $quoteModelRevisionId: UUID!
        $quoteAttachmentRevisionId: UUID!
        $userId: Int
    ) {
        createQuoteModelRevisionAttachment(
            quoteModelRevisionId: $quoteModelRevisionId
            quoteAttachmentRevisionId: $quoteAttachmentRevisionId
            userId: $userId
        ) {
            quoteModelRevision {
                id
            }
            id
        }
    }
`

const updateGeneratedDrawingMutation = gql`
    mutation updateGeneratedDrawingMutation(
        $id: UUID!
        $attachment: AttachmentInput!
    ) {
        updateGeneratedDrawing(id: $id, attachment: $attachment) {
            id
            attachment {
                id
                fileName
                uploadUrl
            }
        }
    }
`

const markUploadedAttachmentMutation = gql`
    mutation markUploadedAttachmentMutation($id: UUID!) {
        markUploadedAttachment(id: $id) {
            id
            fileName
            downloadUrl
        }
    }
`

export function createQuoteModelRevisionAttachment({
    quoteModelRevisionId,
    quoteAttachmentRevisionId,
    refetchQueries,
}) {
    return (dispatch, getState, { client }) => {
        return client
            .mutate({
                mutation: createQMRAttachmentMutation,
                variables: {
                    quoteModelRevisionId: quoteModelRevisionId,
                    quoteAttachmentRevisionId: quoteAttachmentRevisionId,
                },
                refetchQueries: refetchQueries,
            })
            .then(
                result => result.data.createQuoteModelRevisionAttachment || {},
            )
            .catch(error => {
                logger.exception(error, {
                    extra: {
                        info: 'Failed to create createQuoteModelRevisionAttachment',
                    },
                })

                message.error('Failed to upload attachment')
            })
    }
}

export function createQuote() {
    return (dispatch, getState, { client }) => {
        return client
            .mutate({
                mutation: createQuoteMutation,
                variables: {},
            })
            .then(({ data }) => _.get(data, 'createQuote.id'))
            .catch(error => {
                logger.exception(error, {
                    extra: { info: 'Failed to create quote' },
                })

                message.error('Failed to create quote')
            })
    }
}

export function createQuoteModelRevision({
    file,
    units,
    quoteId,
    quoteModelId,
    setAsSelectedForQuoteModel,
    refetchQuery,
    manualRefetch,
    isQueryDetailsQueryRefactorOn,
}) {
    return (dispatch, getState, { client }) => {
        if (isQueryDetailsQueryRefactorOn && manualRefetch) {
            return client
                .mutate({
                    mutation: createQuoteModelRevisionMutation,
                    variables: {
                        uploadMetadata: {
                            referenceId: file.referenceId,
                            contentType: file.type,
                            fileName: file.name,
                            fileSize: file.size,
                            units,
                        },
                        quoteId,
                        quoteModelId,
                        setAsSelectedForQuoteModel,
                    },
                })
                .then(result => {
                    manualRefetch(quoteId)
                    return result.data.createQuoteModelRevision || {}
                })
                .catch(error => {
                    logger.exception(error, {
                        extra: { info: 'Failed to create quoteModelRevision' },
                    })

                    message.error('Failed to upload part')
                })
        } else {
            return client
                .mutate({
                    mutation: createQuoteModelRevisionMutation,
                    variables: {
                        uploadMetadata: {
                            referenceId: file.referenceId,
                            contentType: file.type,
                            fileName: file.name,
                            fileSize: file.size,
                            units,
                        },
                        quoteId,
                        quoteModelId,
                        setAsSelectedForQuoteModel,
                    },
                    refetchQueries: refetchQuery,
                })
                .then(result => {
                    return result.data.createQuoteModelRevision || {}
                })
                .catch(error => {
                    logger.exception(error, {
                        extra: { info: 'Failed to create quoteModelRevision' },
                    })

                    message.error('Failed to upload part')
                })
        }
    }
}

export function createQuoteAttachmentRevision({
    file,
    quoteId,
    attachmentType,
    quoteAttachmentId,
    productAttachmentId,
    refetchQuery,
    adminReplaced,
    userId,
    manualRefetch,
    isQueryDetailsQueryRefactorOn,
}) {
    return (dispatch, getState, { client }) => {
        if (isQueryDetailsQueryRefactorOn && manualRefetch) {
            return client
                .mutate({
                    mutation: createQuoteAttachmentRevisionMutation,
                    variables: {
                        uploadMetadata: {
                            mimeType: file.type,
                            extension: path.extname(file.name),
                            fileName: file.name,
                            type: AttachmentCreateType.user_uploaded,
                        },
                        quoteId,
                        attachmentType,
                        quoteAttachmentId,
                        productAttachmentId,
                        adminReplaced: adminReplaced,
                        userId,
                    },
                })
                .then(result => {
                    manualRefetch(quoteId)
                    return result.data.createQuoteAttachmentRevision || {}
                })
                .catch(error => {
                    logger.exception(error, {
                        extra: {
                            info: 'FileUploader:: Failed to create quoteAttachmentRevision',
                            userId,
                            quoteAttachmentRevisionId:
                                file.quoteAttachmentRevisionId,
                        },
                    })

                    message.error('Failed to upload attachment')
                })
        } else {
            return client
                .mutate({
                    mutation: createQuoteAttachmentRevisionMutation,
                    variables: {
                        uploadMetadata: {
                            mimeType: file.type,
                            extension: path.extname(file.name),
                            fileName: file.name,
                            type: AttachmentCreateType.user_uploaded,
                        },
                        quoteId,
                        attachmentType,
                        quoteAttachmentId,
                        productAttachmentId,
                        adminReplaced: adminReplaced,
                        userId,
                    },
                    refetchQueries: refetchQuery,
                })
                .then(result => result.data.createQuoteAttachmentRevision || {})
                .catch(error => {
                    logger.exception(error, {
                        extra: {
                            info: 'FileUploader:: Failed to create quoteAttachmentRevision',
                            userId,
                            quoteAttachmentRevisionId:
                                file.quoteAttachmentRevisionId,
                        },
                    })

                    message.error('Failed to upload attachment')
                })
        }
    }
}

export function markQuoteAttachmentRevisionUploaded({
    file,
    quoteId,
    refetchQuery,
    userId,
    manualRefetch,
    isQueryDetailsQueryRefactorOn,
}) {
    return (dispatch, getState, { client }) => {
        if (isQueryDetailsQueryRefactorOn && manualRefetch) {
            return client
                .mutate({
                    mutation: markQuoteAttachmentRevisionUploadedMutation,
                    variables: {
                        id: file.quoteAttachmentRevisionId,
                        userId,
                    },
                })
                .then(manualRefetch(quoteId))
                .catch(error => {
                    logger.exception(error, {
                        extra: {
                            info: 'FileUploader:: Failed to mark attachment uploaded',
                            userId,
                            quoteAttachmentRevisionId:
                                file.quoteAttachmentRevisionId,
                        },
                    })
                    message.error(`${file.name} failed to complete the upload`)
                })
        } else {
            return client
                .mutate({
                    mutation: markQuoteAttachmentRevisionUploadedMutation,
                    variables: {
                        id: file.quoteAttachmentRevisionId,
                        userId,
                    },
                    refetchQueries: refetchQuery,
                })
                .catch(error => {
                    logger.exception(error, {
                        extra: {
                            info: 'FileUploader:: Failed to mark attachment uploaded',
                            userId,
                            quoteAttachmentRevisionId:
                                file.quoteAttachmentRevisionId,
                        },
                    })
                    message.error(`${file.name} failed to complete the upload`)
                })
        }
    }
}

export function updateGeneratedDrawing({ file, id }) {
    return (dispatch, getState, { client }) => {
        return client
            .mutate({
                mutation: updateGeneratedDrawingMutation,
                variables: {
                    attachment: {
                        mimeType: file.type,
                        extension: path.extname(file.name),
                        fileName: file.name,
                    },
                    id,
                },
            })
            .then(result => result.data.updateGeneratedDrawing || {})
            .catch(error => {
                logger.exception(error, {
                    extra: {
                        info: 'Failed to update generated drawing',
                    },
                })

                message.error('Failed to upload attachment')
            })
    }
}

export function markUploadedAttachment({
    file,
    refetchQuery,
    userId,
    quoteId,
    manualRefetch,
    isQueryDetailsQueryRefactorOn,
}) {
    return (dispatch, getState, { client }) => {
        if (isQueryDetailsQueryRefactorOn && manualRefetch) {
            return client
                .mutate({
                    mutation: markUploadedAttachmentMutation,
                    variables: {
                        id: file.attachmentId,
                        userId,
                    },
                })
                .then(manualRefetch(quoteId))
                .catch(error => {
                    logger.exception(error, {
                        extra: {
                            info: 'Failed to mark attachment uploaded',
                        },
                    })
                    message.error(`${file.name} failed to complete the upload`)
                })
        } else {
            return client
                .mutate({
                    mutation: markUploadedAttachmentMutation,
                    variables: {
                        id: file.attachmentId,
                        userId,
                    },
                    refetchQueries: refetchQuery,
                })
                .catch(error => {
                    logger.exception(error, {
                        extra: {
                            info: 'Failed to mark attachment uploaded',
                        },
                    })
                    message.error(`${file.name} failed to complete the upload`)
                })
        }
    }
}

export function isPdf(name) {
    const ext = path.extname(name).toLowerCase()

    return ext === '.pdf'
}

export function isValidDfmReportType(name) {
    const ext = path.extname(name).toLowerCase()

    return VALID_DFM_REPORT_FILE_TYPES.includes(ext)
}

export function isValidDfmReportSize(size) {
    const validFileSize = size <= MAX_FILE_SIZE_BYTES

    return validFileSize
}
